/**
 * The rails table: one row per notification stage, for the chosen window.
 *
 * Markup only. Every rule — the columns, the grains, the ratios, the
 * formatting — is in `../data.ts`, where a test can execute it (root jest
 * compiles no `.tsx`).
 *
 * THE TWO GRAINS ARE SHOWN, NOT HIDDEN. The header carries a small "cards" /
 * "email events" band above the column names, because six of these numbers
 * count cards and five count email events, and an operator who reads
 * `delivered` as a share of `emails sent` has computed a rate that does not
 * exist. Saying it once on the page is cheaper than a wrong decision.
 */
import {
  OVERVIEW_WINDOWS,
  RAILS_COLUMNS,
  formatCount,
  formatRate,
  grainBands,
  stageLabel,
  tileBody,
  type OverviewData,
  type OverviewWindowDays,
  type Tile,
} from '../data';
import { TileUnavailable, tileShell } from '../../system-health/components/TileStatus';

interface Props {
  tile: Tile<OverviewData>;
  days: OverviewWindowDays;
  onDaysChange: (days: OverviewWindowDays) => void;
  loading: boolean;
}

export function RailsTable({ tile, days, onDaysChange, loading }: Props) {
  const unavailable = tile.status === 'unavailable';
  const body = tileBody(tile, loading);
  const rows = tile.data?.stages ?? [];
  // Derived from the columns, never counted: see `grainBands` for the defect
  // a pair of counted totals produced.
  const bands = grainBands(RAILS_COLUMNS);

  return (
    <section className={`border rounded-lg p-6 ${tileShell(unavailable, 'bg-card')}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <div>
          <h3 className="text-lg font-semibold">Stages</h3>
          <p className="text-xs text-muted-foreground">
            One row per notification stage. A stage with no traffic is a row of zeroes, not a
            missing row.
          </p>
        </div>
        <div className="flex items-center gap-1" role="group" aria-label="Window">
          {OVERVIEW_WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => onDaysChange(w)}
              aria-pressed={w === days}
              className={`rounded border px-2 py-1 text-xs transition-colors ${
                w === days
                  ? 'border-primary bg-primary/10 font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {w} days
            </button>
          ))}
        </div>
      </div>

      {unavailable && <TileUnavailable error={tile.error} />}

      {body === 'loading' && (
        <p className="text-sm text-muted-foreground">Loading the stages…</p>
      )}

      {body === 'ready' && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm tabular-nums">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-1 text-left font-medium" />
                {/*
                  The key carries the INDEX as well as the grain. `grainBands`
                  runs the columns rather than counting them, so a card column
                  ordered after an event column produces Cards / Email events /
                  Cards — two bands with the same grain, and `key={band.grain}`
                  would then be a duplicate key React cannot reconcile. The
                  column order makes two bands today; the key must not be the
                  thing that breaks the day it makes three.
                */}
                {bands.map((band, i) => (
                  <th
                    key={`${band.grain}-${i}`}
                    colSpan={band.span}
                    className="border-b py-1 text-center font-medium"
                    title={band.meaning}
                  >
                    {band.label}
                  </th>
                ))}
              </tr>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-2 pr-4 text-left font-medium">Stage</th>
                {RAILS_COLUMNS.map((c) => (
                  <th key={c.key} className="px-2 py-2 text-right font-medium" title={c.meaning}>
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.stage} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-4 text-left font-medium whitespace-nowrap">
                    {stageLabel(row.stage)}
                  </td>
                  {RAILS_COLUMNS.map((c) => {
                    const value = row[c.key];
                    const rate =
                      c.denominator === null ? null : formatRate(value, row[c.denominator]);
                    return (
                      <td key={c.key} className="px-2 py-2 text-right align-top">
                        <div>{formatCount(value)}</div>
                        {rate && (
                          <div className="text-[11px] text-muted-foreground">{rate}</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
